import type { TStateCreator } from "../types";

/**
 * User roles in the system
 */
export const UserRoles = {
    ADMIN: "admin",
    FINANCE: "finance",
    BUSINESS_PARTNER: "business_partner",
} as const;

export type TUserRole = (typeof UserRoles)[keyof typeof UserRoles];

/**
 * LocalStorage keys for context switching persistence
 */
const STORAGE_KEY_EFFECTIVE_COMPANY_ID = "proda_effective_company_id";
const STORAGE_KEY_EFFECTIVE_COMPANY_NAME = "proda_effective_company_name";
const STORAGE_KEY_EFFECTIVE_SUBGROUP = "proda_effective_subgroup";
// Legacy key — kept only so we can remove it from storage on startup/clear
const STORAGE_KEY_EFFECTIVE_ROLE_LEGACY = "proda_effective_role";

/**
 * Load context switching state from localStorage
 */
const loadContextSwitchingFromStorage = (): {
    effectiveCompanyId: string | null;
    effectiveCompanyName: string | null;
    effectiveSubgroup: string | null;
} => {
    try {
        const effectiveCompanyId = localStorage.getItem(STORAGE_KEY_EFFECTIVE_COMPANY_ID);
        const effectiveCompanyName = localStorage.getItem(STORAGE_KEY_EFFECTIVE_COMPANY_NAME);
        const effectiveSubgroup = localStorage.getItem(STORAGE_KEY_EFFECTIVE_SUBGROUP);
        return { effectiveCompanyId, effectiveCompanyName, effectiveSubgroup };
    } catch {
        // localStorage might be unavailable (SSR, private browsing, etc.)
        return { effectiveCompanyId: null, effectiveCompanyName: null, effectiveSubgroup: null };
    }
};

/**
 * Save context switching state to localStorage
 */
const saveContextSwitchingToStorage = (
    effectiveCompanyId: string | null,
    effectiveCompanyName: string | null,
    effectiveSubgroup: string | null,
): void => {
    try {
        if (effectiveCompanyId) {
            localStorage.setItem(STORAGE_KEY_EFFECTIVE_COMPANY_ID, effectiveCompanyId);
        } else {
            localStorage.removeItem(STORAGE_KEY_EFFECTIVE_COMPANY_ID);
        }
        if (effectiveCompanyName) {
            localStorage.setItem(STORAGE_KEY_EFFECTIVE_COMPANY_NAME, effectiveCompanyName);
        } else {
            localStorage.removeItem(STORAGE_KEY_EFFECTIVE_COMPANY_NAME);
        }
        if (effectiveSubgroup) {
            localStorage.setItem(STORAGE_KEY_EFFECTIVE_SUBGROUP, effectiveSubgroup);
        } else {
            localStorage.removeItem(STORAGE_KEY_EFFECTIVE_SUBGROUP);
        }
    } catch {
        // localStorage might be unavailable
    }
};

/**
 * Clear context switching state from localStorage (including legacy key)
 */
const clearContextSwitchingFromStorage = (): void => {
    try {
        localStorage.removeItem(STORAGE_KEY_EFFECTIVE_COMPANY_ID);
        localStorage.removeItem(STORAGE_KEY_EFFECTIVE_COMPANY_NAME);
        localStorage.removeItem(STORAGE_KEY_EFFECTIVE_SUBGROUP);
        localStorage.removeItem(STORAGE_KEY_EFFECTIVE_ROLE_LEGACY);
    } catch {
        // localStorage might be unavailable
    }
};

/**
 * Available company for switching
 */
export type TAvailableCompany = {
    id: string;
    name: string;
    slug: string;
    code: string;
    isActive: boolean;
    logoUrl?: string | null;
};

/**
 * User information from authentication
 */
/** Per-company feature flags; an absent key means the feature is disabled. */
export type TCompanyFeatureFlags = {
    email_communications_enabled?: boolean;
    /** Homepage v2 board. Absent means off — the legacy file lists are shown. */
    homepage_v2_enabled?: boolean;
    /** Operating statement view of a TB on the review page. Absent means off. */
    operating_statement_enabled?: boolean;
};

/**
 * Environment feature flags, from /auth/me. These are deployment config set per
 * environment in the Kubernetes configmap — no user can toggle them — which is
 * why they are kept apart from TCompanyFeatureFlags, whose values a Proda admin
 * can change at runtime for a single company. An absent key means disabled.
 */
export type TEnvironmentFeatureFlags = {
    multi_criteria_coa_enabled?: boolean;
};

export type TUser = {
    id: string;
    email: string;
    displayName: string;
    isAdmin: boolean;
    companyId: string;
    companyName: string;
    companyLogoUrl?: string | null;
    isProdaUser: boolean; // User is from proda.ai domain
    role: string;
    subgroup: string | null;
    permissions: string[];
    roleLabels: Record<string, string>;
    subgroupLabels: Record<string, string>;
    subgroupRoles: Record<string, string>; // subgroup -> role (for emulation-aware admin checks)
    emulationTargets: string[];
    assignableSubgroups: string[]; // subgroups this user may assign (create/edit)
    // Feature flags of the EFFECTIVE company, from /auth/me: the user's own
    // company, or the company a Proda user is viewing as (RootLayout refetches
    // /auth/me with X-Company-ID on every switch). Gate on these and nothing
    // else, so the UI shows exactly the features the API will serve.
    companyFeatureFlags: TCompanyFeatureFlags;
    // Environment feature flags, identical for every user of this deployment.
    environmentFeatureFlags: TEnvironmentFeatureFlags;
};

/**
 * Whether the user is acting as an admin, honouring emulation. While emulating a
 * subgroup the user assumes that subgroup's capabilities, so admin status follows
 * the emulated subgroup's role; otherwise it is the user's real admin flag.
 */
export const computeIsViewingAsAdmin = (
    user: Pick<TUser, "isAdmin" | "subgroupRoles"> | null | undefined,
    effectiveSubgroup: string | null,
): boolean => {
    if (!user) return false;
    if (effectiveSubgroup) {
        return user.subgroupRoles[effectiveSubgroup] === "admin";
    }
    return user.isAdmin;
};

/**
 * Auth slice state
 */
export type TAuthState = {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: TUser | null;
    accessToken: string | null;
    error: string | null;
    // Context switching (for debugging/support)
    effectiveCompanyId: string | null; // Overrides user's company for Proda users
    effectiveCompanyName: string | null;
    effectiveSubgroup: string | null; // Overrides user's subgroup for emulation
    availableCompanies: TAvailableCompany[]; // Available companies for Proda users
};

/**
 * Auth slice actions
 */
export type TAuthActions = {
    setUser: (user: TUser) => void;
    setAccessToken: (token: string | null) => void;
    clearUser: () => void;
    setLoading: (isLoading: boolean) => void;
    setError: (error: string | null) => void;
    // Context switching actions
    setEffectiveCompany: (companyId: string | null, companyName: string | null) => void;
    setEffectiveSubgroup: (subgroup: string | null) => void;
    setAvailableCompanies: (companies: TAvailableCompany[]) => void;
    resetContextSwitching: () => void;
};

/**
 * Complete auth slice type
 */
export type TSliceAuth = TAuthState & TAuthActions;

// Load persisted context switching state
const persistedContextSwitching = loadContextSwitchingFromStorage();

const initialState: TAuthState = {
    isAuthenticated: false,
    isLoading: true, // Start as loading until we check auth status
    user: null,
    accessToken: null,
    error: null,
    // Context switching (loaded from localStorage for persistence across reloads)
    effectiveCompanyId: persistedContextSwitching.effectiveCompanyId,
    effectiveCompanyName: persistedContextSwitching.effectiveCompanyName,
    effectiveSubgroup: persistedContextSwitching.effectiveSubgroup,
    availableCompanies: [],
};

/**
 * Creates the authentication slice for the store.
 * Handles user authentication state and actions.
 */
export const createSliceAuth = (): TStateCreator<TSliceAuth> => {
    return (set) => ({
        ...initialState,

        setUser: (user: TUser) => {
            set({
                isAuthenticated: true,
                isLoading: false,
                user,
                error: null,
            });
        },

        setAccessToken: (token: string | null) => {
            set({ accessToken: token });
        },

        clearUser: () => {
            // Clear persisted context switching (including legacy key)
            clearContextSwitchingFromStorage();
            set({
                isAuthenticated: false,
                isLoading: false,
                user: null,
                accessToken: null,
                error: null,
                // Clear context switching
                effectiveCompanyId: null,
                effectiveCompanyName: null,
                effectiveSubgroup: null,
                availableCompanies: [],
            });
        },

        setLoading: (isLoading: boolean) => {
            set({ isLoading });
        },

        setError: (error: string | null) => {
            set({ error, isLoading: false });
        },

        setEffectiveCompany: (companyId: string | null, companyName: string | null) => {
            // Persist to localStorage (preserve existing subgroup value)
            const currentSubgroup = localStorage.getItem(STORAGE_KEY_EFFECTIVE_SUBGROUP);
            saveContextSwitchingToStorage(companyId, companyName, currentSubgroup);
            set({ effectiveCompanyId: companyId, effectiveCompanyName: companyName });
        },

        setEffectiveSubgroup: (subgroup: string | null) => {
            // Persist to localStorage (preserve existing company values)
            const currentCompanyId = localStorage.getItem(STORAGE_KEY_EFFECTIVE_COMPANY_ID);
            const currentCompanyName = localStorage.getItem(STORAGE_KEY_EFFECTIVE_COMPANY_NAME);
            saveContextSwitchingToStorage(currentCompanyId, currentCompanyName, subgroup);
            set({ effectiveSubgroup: subgroup });
        },

        setAvailableCompanies: (companies: TAvailableCompany[]) => {
            set({ availableCompanies: companies });
        },

        resetContextSwitching: () => {
            // Clear persisted context switching (including legacy key)
            clearContextSwitchingFromStorage();
            set({
                effectiveCompanyId: null,
                effectiveCompanyName: null,
                effectiveSubgroup: null,
            });
        },
    });
};
